// Site-wide coordination for tiered image loading (see useProgressiveTierSrc,
// src/features/site/useProgressiveTierSrc.ts). Every image's ~960px "small"
// tier is requested immediately, with no gating — so every block type (hero,
// library content, product gallery, cards) gets a fast first paint. But no
// image anywhere on the page may start fetching its bigger "large"/original
// tier until every small-tier request currently in flight has settled (loaded
// or failed) — otherwise one already-cached image races ahead to full quality
// while a slower one (e.g. the hero) is still working through its own small
// tier, producing an inconsistent half-sharp page instead of a clean two-wave
// load.
//
// A short settle debounce (not an exact zero-pending check) absorbs several
// images registering within the same render commit — without it, the very
// first image to finish loading could open the gate before its siblings
// (mounted in the same commit) have even had a chance to register themselves.
let pending = 0;
let waiters: Array<() => void> = [];
let settleTimer: ReturnType<typeof setTimeout> | null = null;

const SETTLE_MS = 150;
// Backstop so a single wedged request (network hiccup, a tab backgrounded
// mid-load) can never permanently block every OTHER image's quality upgrade.
const HARD_TIMEOUT_MS = 8000;

function scheduleSettleCheck(): void {
  if (settleTimer) clearTimeout(settleTimer);
  settleTimer = setTimeout(() => {
    settleTimer = null;
    if (pending <= 0 && waiters.length) {
      const toRelease = waiters;
      waiters = [];
      toRelease.forEach((resolve) => resolve());
    }
  }, SETTLE_MS);
}

/**
 * Call when a small-tier image request starts loading. Call the returned
 * function exactly once when it settles (load, error, or the component
 * unmounts/moves on before it finished) — safe to call more than once,
 * only the first call counts.
 */
export function registerSmallTierLoad(): () => void {
  pending++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    pending = Math.max(0, pending - 1);
    scheduleSettleCheck();
  };
}

/**
 * Resolves once every small-tier load that's currently in flight (registered
 * via registerSmallTierLoad) has settled — or after a hard timeout, whichever
 * comes first, so a stuck request never wedges every other image's upgrade
 * indefinitely.
 */
export function waitForLargeTierGate(): Promise<void> {
  return new Promise((resolve) => {
    let resolved = false;
    const done = () => {
      if (resolved) return;
      resolved = true;
      resolve();
    };
    waiters.push(done);
    scheduleSettleCheck();
    setTimeout(done, HARD_TIMEOUT_MS);
  });
}
